<script setup lang="ts">
import { ref } from "vue";
import { FileText, Images, LoaderCircle, Paperclip, X } from "lucide-vue-next";
import { useI18n } from "vue-i18n";

withDefaults(
  defineProps<{
    disabled?: boolean;
    mediaDisabled?: boolean;
    fileDisabled?: boolean;
    uploading?: boolean;
    triggerLabel?: string;
    mediaLabel?: string;
    mediaHint?: string;
    mediaAccept?: string;
    fileAccept?: string;
    multipleMedia?: boolean;
  }>(),
  {
    disabled: false,
    mediaDisabled: false,
    fileDisabled: false,
    uploading: false,
    triggerLabel: "",
    mediaLabel: "",
    mediaHint: "",
    mediaAccept: "image/*,video/*",
    fileAccept:
      ".pdf,.txt,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.psd,.ai,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/zip,application/x-zip-compressed,image/vnd.adobe.photoshop,application/postscript",
    multipleMedia: false,
  },
);

const emit = defineEmits<{
  mediaChange: [event: Event];
  fileChange: [event: Event];
}>();
const { t } = useI18n();
const open = ref(false);

function choose(type: "media" | "file", event: Event) {
  open.value = false;
  if (type === "media") emit("mediaChange", event);
  else emit("fileChange", event);
}
</script>

<template>
  <div class="upload-picker">
    <button
      class="upload-trigger"
      type="button"
      :disabled="disabled || uploading || (mediaDisabled && fileDisabled)"
      :title="t('upload.open')"
      :aria-label="t('upload.open')"
      @click="open = true"
    >
      <LoaderCircle v-if="uploading" :size="18" class="spin" />
      <Paperclip v-else :size="18" />
      <span v-if="triggerLabel">{{ triggerLabel }}</span>
    </button>
    <Teleport to="body">
      <div v-if="open" class="upload-backdrop" @click.self="open = false">
        <section
          class="upload-sheet"
          role="dialog"
          aria-modal="true"
          :aria-label="t('upload.chooseSource')"
        >
          <header>
            <b>{{ t("upload.chooseSource") }}</b
            ><button
              type="button"
              :title="t('common.close')"
              @click="open = false"
            >
              <X :size="20" />
            </button>
          </header>
          <label :class="{ disabled: mediaDisabled }">
            <Images :size="22" />
            <span
              ><b>{{ mediaLabel || t("upload.media") }}</b
              ><small>{{ mediaHint || t("upload.mediaHint") }}</small></span
            >
            <input
              type="file"
              :accept="mediaAccept"
              :multiple="multipleMedia"
              :disabled="mediaDisabled"
              @change="choose('media', $event)"
            />
          </label>
          <label :class="{ disabled: fileDisabled }">
            <FileText :size="22" />
            <span
              ><b>{{ t("upload.file") }}</b
              ><small>{{ t("upload.fileHint") }}</small></span
            >
            <input
              type="file"
              :accept="fileAccept || undefined"
              :disabled="fileDisabled"
              @change="choose('file', $event)"
            />
          </label>
          <button class="cancel" type="button" @click="open = false">
            {{ t("common.cancel") }}
          </button>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.upload-picker {
  display: inline-flex;
  width: max-content;
  align-items: flex-end;
}
.upload-trigger {
  display: inline-flex;
  min-width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid var(--line);
  padding: 0 10px;
  background: #fff;
  color: var(--ink);
  cursor: pointer;
}
.upload-trigger:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}
.upload-trigger span {
  font-weight: 650;
  white-space: nowrap;
}
.upload-backdrop {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(17, 25, 32, 0.46);
}
.upload-sheet {
  display: grid;
  gap: 8px;
  width: min(400px, 100%);
  padding: 14px;
  background: #fff;
  box-shadow: 0 16px 50px rgba(16, 30, 36, 0.22);
}
.upload-sheet header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 2px 8px;
}
.upload-sheet header button {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  background: transparent;
}
.upload-sheet label {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 64px;
  border: 1px solid var(--line);
  padding: 10px 12px;
  color: var(--ink);
  cursor: pointer;
}
.upload-sheet label:hover {
  border-color: var(--brand);
  background: #f8fbfa;
}
.upload-sheet label.disabled {
  cursor: not-allowed;
  opacity: 0.45;
}
.upload-sheet label span,
.upload-sheet label b,
.upload-sheet label small {
  display: block;
}
.upload-sheet label small {
  margin-top: 3px;
  color: var(--muted);
}
.upload-sheet input {
  display: none;
}
.cancel {
  min-height: 42px;
  border: 0;
  background: #f2f5f5;
  color: var(--ink);
  font-weight: 650;
}
@media (max-width: 700px) {
  .upload-backdrop {
    align-items: end;
    padding: 0;
  }
  .upload-sheet {
    width: 100%;
    padding: 14px 14px calc(14px + env(safe-area-inset-bottom));
    box-shadow: 0 -12px 36px rgba(16, 30, 36, 0.2);
  }
  .upload-sheet label {
    min-height: 68px;
  }
  .upload-trigger {
    height: 42px;
  }
}
</style>
